import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Autocomplete, Dialog, DialogContent, IconButton } from "@mui/material";
import Grid from '@mui/material/Grid';
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { DocumentUploadService } from "../../../Component/Api/DocumentUpload/DocumentUploadService";
import { FormField } from '../../../Component/Common/Components/FormField';
import { MDAlert } from '../../../Component/Common/Components/MDAlert';
import { MDBox } from '../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../Component/Common/Components/MDTypography';
import { Tooltip } from '../../../Component/Common/Components/Tooltip/Tooltip';
import { UploadButton } from '../../../Component/Common/Components/UploadButton/UploadButton';
import { DocumentDto, DocumentSearchRequest, DocumentUploadParameters } from "../../../Component/Model/Dto/Document";
import config from "../../../config";
import { CustomDialogTitle } from '../Custom/CustomDialogTitle';
import { UserActivityService } from '../../../Component/Api/UserActivity/UserActivityService';
import { UserActivityActionType } from '../../../Component/Model/Enum/BEE123Enums';

interface IProps {
    handleCancel: () => void;
    link: string;
    display: boolean;
    id: string;
    scorecardId: string;
    isEEModule?: boolean;
    section?: string;
    attachedFile?: boolean;
}

export const SupportingDocuments = (props: IProps) => {

    const [documentTypes, setDocumentTypes] = React.useState<DocumentDto[]>([]);
    const [documentsUploaded, setDocumentsUploaded] = React.useState<DocumentDto[]>([]);
    const [documentsNotUploaded, setDocumentsNotUploaded] = React.useState<DocumentDto[]>([]);
    const [showUploadButton, setShowUploadButton] = React.useState<boolean>(false);
    const [documentTypeId, setDocumentTypeId] = React.useState<string>('');
    const [isEEModule] = React.useState<boolean>(props.isEEModule != null ? props.isEEModule : false)
    const hasLogged = React.useRef(false);

    const documentSearchRequest: DocumentSearchRequest = {
        id: props.id,
        link: props.link,
        scorecardId: props.scorecardId
    };
    const documentUploadRequest: DocumentUploadParameters = {
        ...documentSearchRequest,
        documentTypeId: documentTypeId
    };

    const documentsLog = (actionType: number, actionDescription: string) => {
        props.section && UserActivityService.createActivityLog({
            section: props.section,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: props.scorecardId,
            requestUrl: window.location.href
        });
    }

    const uploadFile = async (document: any) => {
        const headers = { 'Content-Type': 'multipart/form-data' };

        let formData = new FormData();
        if (isEEModule) {
            formData.append(document.name, document);
        }
        else {
            formData.append('Id', props.id.toString());
            formData.append('Link', props.link);
            formData.append('File', document, document.name);
            formData.append('DocumentTypeId', documentTypeId.toString());
            formData.append('ScorecardId', props.scorecardId);
        }
        try {
            if (isEEModule) {
                await DocumentUploadService.SupportingDocumentsFileUpload(formData, documentUploadRequest, headers);
            }
            else {
                await DocumentUploadService.CoreSupportingDocumentsFileUpload(formData, documentUploadRequest, headers);
            }
            const logText = props.attachedFile ? "Attach file for member" : "Add supporting document";
            props.section && documentsLog(UserActivityActionType.Upload, logText);
            toast.success("File successfully uploaded ");
            getUploadedDocuments();
        } catch (error) {
            console.log(error)
        }

    }

    const onFileUpload = (event: any) => {
        if (event.target) {
            const document = event.target.files[0];
            const fileSizeInKB = Math.ceil(document.size / 1024);
            const maxFileSizeInKB = 1024 * 20;
            let fileExt = document.name.split('.').pop();
            const allowedFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'doc', 'docx', 'xls', 'xlsx', 'pdf', 'zip', 'rar']
            if (allowedFileTypes.includes(fileExt.toLowerCase())) {

                if (fileSizeInKB > maxFileSizeInKB) {
                    toast.error("File is bigger than 20MB");
                    return;
                }
                else {
                    uploadFile(document);
                    return;
                }
            }
            else {
                toast.error("Unsupported file type. Permitted file types are: .jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .xls, .xlsx, .pdf, .zip, .rar");
                return;
            }
        }
    }
    const getDocument = (id: number) => {
        const url = `${config.apiUrl}/DocumentUpload/Get?id=${id}&scorecardId=${props.scorecardId}`;
        window.open(url);
        (props.section && !props.attachedFile) && documentsLog(UserActivityActionType.Download, "Download supporting document");
    }

    const deleteDocument = async (id: number) => {
        try {
            await DocumentUploadService.DeleteDocument(id);
            const logText = props.attachedFile ? "Delete file for member" : "Delete supporting document";
            props.section && documentsLog(UserActivityActionType.Delete, logText);
            toast.success("File successfully deleted ");
            getUploadedDocuments();

        } catch (error) {
            console.log(error)
        }
    }

    const getDocumentTypes = async () => {
        try {
            const results = await DocumentUploadService.GetDocumentTypesForSection(props.link);
            setDocumentTypes(results.data);

        } catch (error) {
            console.log(error)
        }

    }

    const getUploadedDocuments = async () => {
        try {
            const results = await DocumentUploadService.GetDocumentsUploaded(documentSearchRequest);
            setDocumentsNotUploaded(results.data.DocumentsNotUploaded);
            setDocumentsUploaded(results.data.DocumentsUploaded);

        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        getDocumentTypes();
        getUploadedDocuments();
        if (props.section && !hasLogged.current && !props.attachedFile) {
            hasLogged.current = true;
            documentsLog(UserActivityActionType.View, "View list of supporting documents");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (

        <Dialog open={props.display} onClose={props.handleCancel} aria-labelledby="form-dialog-title" maxWidth='lg' fullWidth>
            <CustomDialogTitle id="customized-dialog-title" onClose={props.handleCancel}>
                Supporting Documents
            </CustomDialogTitle>
            <DialogContent style={{ margin: "10px" }}>
                <Grid container justifyContent='center' spacing={5}>
                    <Grid item xs={6} lg={6}>
                        <MDBox>
                            <MDBox mb={2}>
                                <Autocomplete
                                    style={{ paddingTop: "5px" }}
                                    options={documentTypes}
                                    getOptionLabel={option => option.documentName}
                                    onChange={(e: any, newValue: any) => {
                                        if (newValue) {
                                            setShowUploadButton(true);
                                            setDocumentTypeId(newValue.documentTypeId);
                                        } else {
                                            setShowUploadButton(false);
                                        }
                                    }}
                                    renderOption={(props, option) => {
                                        return (
                                            <li {...props} key={option.documentTypeId}>
                                                {option.documentName}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => <FormField variant="outlined" label='Please select the type of document to be uploaded' {...params} type='text' required />}
                                />
                            </MDBox>
                            {showUploadButton && <MDBox mt={3.5}>
                                <UploadButton
                                    id='upload-file' onChange={onFileUpload} type='file'
                                    accept=".jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .xls, .xlsx, .pdf, .zip, .rar"
                                    IconView={FileUploadIcon} text='Select file'
                                    color="info"
                                    disabled={false}>
                                </UploadButton>
                            </MDBox>}
                            <MDBox display="flex" mt={4} color="white">
                                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='light'>

                                    <p> The maximum file size for any upload is <strong>20MB</strong> per file.</p>

                                    <MDTypography component='span' variant='body' fontWeight='bold' fontSize='0.95rem' mb={1} display="flex" alignItems="center">
                                        Only the following file types are allowed:
                                    </MDTypography>

                                    <p> *.jpeg / *.jpg / *.png / *.gif / *.bmp / *.tiff / *.doc / *.docx / *.xls / *.xlsx / *.pdf / *.zip / *.rar / </p>

                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </Grid>
                    <Grid item xs={6} lg={6}>
                        <MDBox>
                            <MDBox mb={3}>
                                <MDAlert color='light'>
                                    <MDTypography component='span' variant='body' fontSize='0.83rem' fontWeight='regular' >

                                        <MDTypography component='span' variant='body' fontSize='0.87rem' fontWeight='medium' mb={1} display="flex" alignItems="center" >
                                            The following supporting documents should be uploaded:
                                        </MDTypography>

                                        {documentsNotUploaded.length === 0 && <p>All mandatory documents have been successfully uploaded, there are no missing items.</p>}

                                        <ul>{
                                            documentsNotUploaded.map((type, index) =>
                                                <li key={index} style={{ margin: '3px', marginBottom: '10px' }}>
                                                    {type.documentType}
                                                </li>
                                            )
                                        }</ul>

                                    </MDTypography>
                                </MDAlert>
                            </MDBox>
                        </MDBox>

                        <MDBox >
                            <MDAlert color='info' >
                                <MDTypography component='span' variant='body' fontSize='0.83rem' fontWeight='regular' color style={{ width: '100%' }}>

                                    <MDTypography component='span' variant='body' fontSize='0.87rem' fontWeight='medium' mb={1} display="flex" alignItems="center" color>
                                        The following files have been uploaded:
                                    </MDTypography>

                                    <ul>{
                                        documentsUploaded.map((doc, index) => {
                                            return <li key={index} style={{ margin: '3px', marginBottom: '10px' }}>
                                                {doc.showTooltip ?
                                                    <Tooltip title={doc.fullNameAndType}>
                                                        <button
                                                            style={{ background: 'none', color: 'inherit', cursor: 'pointer', border: 'none' }}
                                                            onClick={() => getDocument(doc.documentId)}>{doc.shortenedName}
                                                        </button>
                                                    </Tooltip> :
                                                    <button
                                                        style={{ background: 'none', color: 'inherit', cursor: 'pointer', border: 'none' }}
                                                        onClick={() => getDocument(doc.documentId)}>{doc.fullNameAndType}
                                                    </button>
                                                }

                                                <IconButton
                                                    aria-label="close" size="small"
                                                    style={{ float: 'right', color: 'inherit' }}
                                                    onClick={() => deleteDocument(doc.documentId)}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </li>;
                                        }
                                        )
                                    }</ul>

                                </MDTypography>
                            </MDAlert>
                        </MDBox>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>

    )
}
